<template>
  <div class="device-management">
    <div class="page-header flex justify-between items-center mb-4">
      <div class="flex items-center align-title">
        <h2 class="text-xl font-bold mr-4">设备管理</h2>
      </div>
    </div>

    <!-- 设备分类统计卡片 -->
    <div class="device-stats flex mb-6">
      <el-card shadow="hover" class="stat-card" :body-style="{ padding: '15px' }">
        <div class="flex items-center">
          <div class="stat-icon">
            <i class="el-icon-monitor text-blue-500"></i>
          </div>
          <div class="stat-content">
            <div class="stat-title text-gray-500">设备总数</div>
            <div class="stat-value text-2xl font-bold">{{ deviceStats.total }}</div>
          </div>
        </div>
      </el-card>
      
      <el-card shadow="hover" class="stat-card" :body-style="{ padding: '15px' }">
        <div class="flex items-center">
          <div class="stat-icon">
            <i class="el-icon-success text-green-500"></i>
          </div>
          <div class="stat-content">
            <div class="stat-title text-gray-500">在线设备</div>
            <div class="stat-value text-2xl font-bold">{{ deviceStats.online }}</div>
          </div>
        </div>
      </el-card>
      
      <el-card shadow="hover" class="stat-card" :body-style="{ padding: '15px' }">
        <div class="flex items-center">
          <div class="stat-icon">
            <i class="el-icon-error text-red-500"></i>
          </div>
          <div class="stat-content">
            <div class="stat-title text-gray-500">离线设备</div>
            <div class="stat-value text-2xl font-bold">{{ deviceStats.offline }}</div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 设备过滤和搜索 -->
    <div class="filter-section flex justify-between mb-4">
      <div class="flex">
        <el-select v-model="filters.deviceType" placeholder="设备类型" clearable class="mr-3 w-40" @change="handleFilter">
          <el-option v-for="item in deviceTypes" :key="item.value" :label="item.label" :value="item.value"></el-option>
        </el-select>
        
        <el-select v-model="filters.status" placeholder="设备状态" clearable class="mr-3 w-40" @change="handleFilter">
          <el-option 
            v-for="item in [
              { value: 'online', label: '在线' },
              { value: 'offline', label: '离线' }
            ]" 
            :key="item.value" 
            :label="item.label" 
            :value="item.value">
          </el-option>
        </el-select>
      </div>
      
      <el-input
        v-model="filters.searchText"
        placeholder="搜索设备名称/编码/位置"
        prefix-icon="el-icon-search"
        clearable
        @input="handleFilter"
        class="w-80"
      ></el-input>
    </div>

    <!-- 设备表格，设置最大高度，超出时显示滚动条 -->
    <el-table
      :data="filteredDevices"
      :border="true"
      style="width: 100%"
      v-loading="loading"
      row-key="id"
      :max-height="tableMaxHeight"
      class="device-table"
    >
      <el-table-column label="设备名称" prop="name" min-width="180">
        <template slot-scope="scope">
          <div class="device-name-cell">
            <div class="device-icon mr-2">
              <i :class="getDeviceIcon(scope.row.device_type)"></i>
            </div>
            <span>{{ scope.row.name }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="设备编码" prop="device_code" min-width="120"></el-table-column>
      <el-table-column label="设备类型" prop="device_type" min-width="120">
        <template slot-scope="scope">
          {{ getDeviceTypeLabel(scope.row.device_type) }}
        </template>
      </el-table-column>
      <el-table-column label="主机地址" prop="host" min-width="150">
        <template slot-scope="scope">
          {{ scope.row.device_type === 'FLOWMETER' ? '设备直传' : (scope.row.host || '-') }}
        </template>
      </el-table-column>
      <el-table-column label="端口" prop="port" width="100">
        <template slot-scope="scope">
          {{ scope.row.device_type === 'FLOWMETER' ? '-' : (scope.row.port || '-') }}
        </template>
      </el-table-column>
      <el-table-column label="设备位置" prop="location" min-width="150"></el-table-column>
      <el-table-column label="状态" width="150">
        <template slot-scope="scope">
          <div class="status-cell">
            <device-status-control
              :status="hasAdminAccess ? scope.row.status : 'online'"
              :deviceId="scope.row.id"
            />
            <el-button
              v-if="scope.row.device_type === 'LASER' && isAdmin"
              type="text"
              size="mini"
              class="status-detail-btn"
              @click="handleOpenLaserStatusForDevice(scope.row)"
            >详情</el-button>
          </div>
        </template>
      </el-table-column>
      <el-table-column v-if="hasAdminAccess" label="启用状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tooltip :content="getEnabledTooltip(scope.row.enabled)" placement="top" :open-delay="500">
            <span>
              <el-switch
                v-model="scope.row.enabled"
                :active-value="1"
                :inactive-value="0"
                :disabled="!hasAdminAccess"
                @change="handleEnabledChange(scope.row)"
              ></el-switch>
            </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" fixed="right" v-if="hasAdminAccess">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="handleEditDevice(scope.row)">
            <i class="el-icon-edit mr-1"></i>编辑
          </el-button>
          <el-button type="text" size="small" class="text-red-500" @click="handleDeleteDevice(scope.row)">
            <i class="el-icon-delete mr-1"></i>删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 添加/编辑设备对话框 -->
    <el-dialog
      :title="dialogStatus === 'create' ? '添加设备' : '编辑设备'"
      :visible.sync="dialogVisible"
      width="820px"
      @closed="handleDialogClosed"
    >
      <device-form
        ref="deviceForm"
        :deviceData="currentDevice"
        :bridgeId="selectedBridgeId"
        @submit="handleFormSubmit"
        @cancel="dialogVisible = false"
        @open-channels="openChannelsByNvr"
      />
    </el-dialog>

    <nvr-channel-drawer ref="chDrawer" :nvr="chNvr" />

    <laser-status-dialog
      :visible.sync="laserStatusVisible"
      :bridge-id="selectedBridgeId"
      :device-id="laserStatusDeviceId"
      :device-name="laserStatusDeviceName"
    />

  </div>
</template>

<script>
import deviceAPI from '@/api/device'
import { DEVICE_TYPES, DEVICE_STATUS } from '@/config/device-constants'
import DeviceForm from '@/components/device/DeviceForm'
import DeviceStatusControl from '@/components/device/DeviceStatusControl'
import NvrChannelDrawer from '@/components/device/NvrChannelDrawer.vue'
import LaserStatusDialog from '@/components/device/LaserStatusDialog.vue'
import videoAPI from '@/api/video'

export default {
  name: 'DeviceManagement',
  components: {
    DeviceForm,
    DeviceStatusControl,
    NvrChannelDrawer,
    LaserStatusDialog
  },
  inject: {
    registerNavButtons: {
      default: () => () => {}
    },
    unregisterNavButtons: {
      default: () => () => {}
    }
  },
  data() {
    return {
      devices: [],
      filteredDevices: [],
      selectedBridgeId: null,
      selectedBridge: null,
      loading: false,
      dialogVisible: false,
      dialogStatus: 'create', // 'create' 或 'edit'
      currentDevice: {},
      deviceTypes: DEVICE_TYPES,
      deviceStatusOptions: DEVICE_STATUS,
      filters: {
        deviceType: '',
        status: '',
        searchText: ''
      },
      deviceStats: {
        total: 0,
        online: 0,
        offline: 0
      },
      tableMaxHeight: 500, // 默认表格高度
      nvrList: [],
      chNvr: null,
      hasAdminAccess: false, // 是否有管理员权限（ADMIN或MONITOR）
      isAdmin: false, // 是否系统管理员（仅ADMIN），用于控制"详情"入口
      statusPollingTimer: null, // 设备状态轮询定时器
      laserStatusVisible: false,
      laserStatusDeviceId: null,
      laserStatusDeviceName: ''
    }
  },
  mounted() {
    // 检查用户权限
    this.checkAdminAccess();
    
    // 从localStorage获取桥梁信息
    this.loadSavedBridge();
    
    // 监听桥梁变更事件
    this.$root.$on('bridge-changed', this.handleBridgeChanged);
    
    // 计算表格最大高度
    this.calculateTableHeight()
    // 监听窗口大小改变事件
    window.addEventListener('resize', this.calculateTableHeight)
    
    // 注册导航按钮
    this.registerNavigationButtons()
    
    // 启动设备状态轮询
    this.startStatusPolling()
  },
  beforeDestroy() {
    // 移除窗口大小改变事件监听
    window.removeEventListener('resize', this.calculateTableHeight)
    
    // 移除事件监听
    this.$root.$off('bridge-changed', this.handleBridgeChanged)
    
    // 注销导航按钮
    this.unregisterNavButtons()
    
    // 停止设备状态轮询
    this.stopStatusPolling()
  },
  methods: {
    // 检查用户权限
    checkAdminAccess() {
      try {
        const hasAdminAccess = localStorage.getItem('hasAdminAccess');
        this.hasAdminAccess = hasAdminAccess === 'true';
        this.isAdmin = localStorage.getItem('isAdmin') === 'true';
      } catch (error) {
        console.error('获取用户权限失败:', error);
        this.hasAdminAccess = false;
        this.isAdmin = false;
      }
    },
    // 注册导航按钮
    registerNavigationButtons() {
      const buttons = [
        {
          id: 'refresh-devices',
          text: '刷新',
          icon: 'el-icon-refresh',
          loading: false,
          handler: this.handleRefreshDevices
        }
      ];
      
      // 只有管理员权限才显示添加设备按钮
      if (this.hasAdminAccess) {
        buttons.unshift({
          id: 'add-device',
          text: '添加设备',
          icon: 'el-icon-plus',
          type: 'primary',
          handler: this.handleAddDevice
        });
      }
      
      this.registerNavButtons(buttons);
    },
    // 加载保存的桥梁信息
    loadSavedBridge() {
      const savedBridge = localStorage.getItem('selectedBridge');
      if (savedBridge) {
        try {
          this.selectedBridge = JSON.parse(savedBridge);
          this.selectedBridgeId = this.selectedBridge.id;
          if (this.selectedBridgeId) {
            this.fetchDevices();
          }
        } catch (error) {
          console.error('解析保存的桥梁数据出错', error);
        }
      }
    },
    // 处理桥梁变更事件
    handleBridgeChanged(bridge) {
      if (bridge && bridge.id) {
        this.selectedBridge = bridge;
        this.selectedBridgeId = bridge.id;
        this.fetchDevices();
        // 重新启动状态轮询
        this.startStatusPolling();
      }
    },
    // 打开激光状态巡检（聚焦指定激光）
    handleOpenLaserStatusForDevice(row) {
      if (!row || !row.id) {
        return
      }
      if (!this.isAdmin) {
        return
      }
      this.laserStatusDeviceId = row.id
      this.laserStatusDeviceName = row.name || row.device_name || ''
      this.laserStatusVisible = true
    },
    // 刷新设备列表
    async handleRefreshDevices() {
      if (!this.selectedBridgeId) {
        this.$message.warning('请先选择桥梁')
        return
      }
      
      // 获取导航按钮组并更新状态
      const buttons = [
        {
          id: 'refresh-devices',
          text: '刷新',
          icon: 'el-icon-refresh',
          loading: true,
          handler: this.handleRefreshDevices
        }
      ];
      
      // 只有管理员权限才显示添加设备按钮
      if (this.hasAdminAccess) {
        buttons.unshift({
          id: 'add-device',
          text: '添加设备',
          icon: 'el-icon-plus',
          type: 'primary',
          handler: this.handleAddDevice
        });
      }
      
      this.registerNavButtons(buttons);
      
      try {
        await this.fetchDevices();
        this.$message.success('刷新成功');
      } finally {
        // 更新按钮状态，取消加载中
        const refreshButton = buttons.find(button => button.id === 'refresh-devices');
        if (refreshButton) {
          refreshButton.loading = false;
        }
        this.registerNavButtons(buttons);
      }
    },
    // 计算表格最大高度，以避免页面出现滚动条
    calculateTableHeight() {
      this.$nextTick(() => {
        // 获取视窗高度
        const windowHeight = window.innerHeight
        // 获取表格到顶部的距离
        const tableElement = this.$el.querySelector('.device-table')
        if (tableElement) {
          const tableTop = tableElement.getBoundingClientRect().top
          // 底部预留20px空间
          this.tableMaxHeight = windowHeight - tableTop - 20
        }
      })
    },
    async fetchDevices() {
      if (!this.selectedBridgeId) {
        this.$message.warning('请先选择桥梁')
        return
      }
      
      this.loading = true
      try {
        const response = await deviceAPI.getDevicesByBridge(this.selectedBridgeId)
        try {
          const nr = await videoAPI.cfgListNvr({ bridgeId: this.selectedBridgeId })
          this.nvrList = nr.data || []
        } catch (e) { this.nvrList = [] }
        if (response.code === 200 && response.data) {
          this.devices = response.data
          this.devices.forEach(d => {
            d._nvr = d.device_type === 'CAMERA' ? this.matchNvr(d) : null
          })
          this.applyFilters()
          this.calculateDeviceStats()
        } else {
          this.$message.error('获取设备列表失败：' + (response.message || '未知错误'))
        }
      } catch (error) {
        console.error('获取设备列表出错:', error)
        this.$message.error('获取设备列表出错：' + (error.message || '浏览器网络有波动，请稍后再试'))
      } finally {
        this.loading = false
      }
    },
    calculateDeviceStats() {
      // 重置统计数据
      this.deviceStats = {
        total: this.devices.length,
        online: 0,
        offline: 0
      }
      
      if (this.hasAdminAccess) {
        // 管理员权限：显示真实状态
        this.devices.forEach(device => {
          if (device.status === 'online') {
            this.deviceStats.online++
          } else {
            // 所有非在线设备都视为离线
            this.deviceStats.offline++
            // 确保状态只有online和offline
            device.status = 'offline'
          }
        })
      } else {
        // 非管理员权限：所有设备显示为在线，离线数为0
        this.deviceStats.online = this.devices.length
        this.deviceStats.offline = 0
        // 修改设备状态为在线（仅用于显示）
        this.devices.forEach(device => {
          device.displayStatus = 'online'
        })
      }
    },
    matchNvr(device) {
      const code = device.device_code
      if (!code) return null
      const port = Number(device.port) || null
      return this.nvrList.find(n => String(n.nvr_code) === String(code))
        || this.nvrList.find(n => n.name === code)
        || this.nvrList.find(n => port && Number(n.web_port) === port)
        || this.nvrList.find(n => port && Number(n.rtsp_port) === port)
        || null
    },
    openChannels(row) {
      this.chNvr = row._nvr
      this.$nextTick(() => { this.$refs.chDrawer.open() })
    },
    openChannelsByNvr(nvrMeta) {
      const n = this.nvrList.find(x => x.id === nvrMeta.id) || nvrMeta
      this.chNvr = n
      this.$nextTick(() => { this.$refs.chDrawer.open() })
    },
    async persistNvr(formData) {
      const nvr = formData.nvr
      let nvrId = nvr.id
      if (!nvrId) {
        const port = Number(formData.port) || null
        const m = this.nvrList.find(x => port && Number(x.web_port) === port)
          || this.nvrList.find(x => port && Number(x.rtsp_port) === port)
          || null
        if (m) nvrId = m.id
      }
      const body = {
        bridge_id: Number(formData.bridgeId),
        nvr_code: String(formData.device_code),
        name: formData.name,
        host: formData.host,
        web_port: Number(formData.port),
        rtsp_host: nvr.rtsp_host || null,
        rtsp_port: nvr.rtsp_port || null,
        username: nvr.username || 'admin',
        password: nvr.password,
        remark: nvr.remark || '',
        enabled: Number(formData.enabled)
      }
      if (nvrId) {
        if (body.password) {
          await videoAPI.cfgUpdateNvr(nvrId, body)
        } else {
          const b2 = { ...body }
          delete b2.password
          await videoAPI.cfgUpdateNvr(nvrId, b2)
        }
        return nvrId
      }
      body.channel_count = nvr.channel_count || 8
      const r = await videoAPI.cfgCreateNvr(body)
      return (r.data && r.data.id) || null
    },
    handleFilter() {
      this.applyFilters()
    },
    applyFilters() {
      // 应用过滤条件
      let result = [...this.devices]
      
      // 按设备类型过滤
      if (this.filters.deviceType) {
        result = result.filter(device => device.device_type === this.filters.deviceType)
      }
      
      // 按状态过滤
      if (this.filters.status) {
        if (this.hasAdminAccess) {
          result = result.filter(device => device.status === this.filters.status)
        } else {
          // 非管理员权限：所有设备状态都是在线，只有选择在线时才显示设备
          if (this.filters.status === 'online') {
            // 保持所有设备，因为非管理员看到的都是在线
          } else {
            // 选择离线时，不显示任何设备，因为非管理员看不到离线设备
            result = []
          }
        }
      }
      
      // 按搜索文本过滤
      if (this.filters.searchText) {
        const searchText = this.filters.searchText.toLowerCase()
        result = result.filter(device => 
          (device.name && device.name.toLowerCase().includes(searchText)) ||
          (device.device_code && device.device_code.toLowerCase().includes(searchText)) ||
          (device.location && device.location.toLowerCase().includes(searchText))
        )
      }
      
      this.filteredDevices = result
    },
    handleAddDevice() {
      if (!this.hasAdminAccess) {
        this.$message.warning('您没有权限执行此操作')
        return
      }
      this.dialogStatus = 'create'
      this.currentDevice = {}
      this.dialogVisible = true
    },
    handleEditDevice(device) {
      if (!this.hasAdminAccess) {
        this.$message.warning('您没有权限执行此操作')
        return
      }
      this.dialogStatus = 'edit'
      this.currentDevice = { ...device }
      this.dialogVisible = true
    },
    async handleDeleteDevice(device) {
      if (!this.hasAdminAccess) {
        this.$message.warning('您没有权限执行此操作')
        return
      }
      const sharedNvr = device.device_type === 'CAMERA' && device._nvr
        && this.devices.some(d => d.id !== device.id && d.device_type === 'CAMERA' && d._nvr && d._nvr.id === device._nvr.id)
      const tip = device.device_type === 'CAMERA' && device._nvr
        ? (sharedNvr
          ? `确定要删除设备"${device.name}"吗？其视频配置（NVR #${device._nvr.nvr_code}）与其它设备共享，将保留。`
          : `确定要删除设备"${device.name}"吗？将同时停用其视频配置（NVR #${device._nvr.nvr_code}）。`)
        : `确定要删除设备"${device.name}"吗？删除后无法恢复。`
      this.$confirm(tip, '删除确认', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          if (device.device_type === 'CAMERA' && device._nvr) {
            const shared = this.devices.some(d =>
              d.id !== device.id && d.device_type === 'CAMERA' && d._nvr && d._nvr.id === device._nvr.id)
            if (!shared) {
              try { await videoAPI.cfgDeleteNvr(device._nvr.id) } catch (e) { /* 不阻断设备删除 */ }
            }
          }
          const response = await deviceAPI.deleteDevice(device.id)
          if (response.code === 200 && response.data) {
            this.$message.success('删除设备成功')
            await this.fetchDevices()
          } else {
            this.$message.error('删除设备失败：' + (response.message || '未知错误'))
          }
        } catch (error) {
          console.error('删除设备出错:', error)
          this.$message.error('删除设备出错：' + (error.message || '浏览器网络有波动，请稍后再试'))
        }
      }).catch(() => {
        // 用户取消
      })
    },
    async handleEnabledChange(device) {
      if (!this.hasAdminAccess) {
        // 回滚UI状态
        device.enabled = device.enabled === 1 ? 0 : 1
        this.$message.warning('您没有权限执行此操作')
        return
      }
      
      try {
        // 深拷贝设备对象以避免直接修改
        const updatedDevice = { ...device }
        
        // 更新设备
        const response = await deviceAPI.updateDevice(device.id, updatedDevice)
        if (response.code === 200 && response.data) {
          this.$message.success(`${device.enabled === 1 ? '启用' : '禁用'}设备成功`)
        } else {
          // 操作失败，回滚UI状态
          device.enabled = device.enabled === 1 ? 0 : 1
          this.$message.error(`${device.enabled === 0 ? '启用' : '禁用'}设备失败：` + (response.message || '未知错误'))
        }
      } catch (error) {
        // 操作出错，回滚UI状态
        device.enabled = device.enabled === 1 ? 0 : 1
        console.error('更新设备启用状态出错:', error)
        this.$message.error('更新设备启用状态出错：' + (error.message || '浏览器网络有波动，请稍后再试'))
      }
    },
    async handleFormSubmit(formData) {
      if (!this.hasAdminAccess) {
        this.$message.warning('您没有权限执行此操作')
        return
      }

      try {
        let response
        if (!formData.extend_params|| formData.extend_params === '') {
          formData.extend_params = null
        }
        const isCamera = formData.device_type === 'CAMERA'
        let nvrSaved = false
        if (isCamera) {
          try {
            await this.persistNvr(formData)
            nvrSaved = true
          } catch (e) {
            const msg = (e.response && e.response.data && e.response.data.msg) || e.message || e
            this.$message.error('视频配置保存失败：' + msg)
            return
          }
        }
        delete formData.nvr
        if (this.dialogStatus === 'create') {
          response = await deviceAPI.createDevice(formData)
          if (response.code === 200 && response.data) {
            this.$message.success('添加设备成功' + (nvrSaved ? '（含视频配置，30s 内生效）' : ''))
            this.dialogVisible = false
            await this.fetchDevices()
          } else {
            this.$message.error('添加设备失败：' + (response.message || '未知错误'))
          }
        } else {
          response = await deviceAPI.updateDevice(formData.id, formData)
          if (response.code === 200 && response.data) {
            this.$message.success('更新设备成功' + (nvrSaved ? '（含视频配置，30s 内生效）' : ''))
            this.dialogVisible = false
            await this.fetchDevices()
          } else {
            this.$message.error('更新设备失败：' + (response.message || '未知错误'))
          }
        }
      } catch (error) {
        console.error('保存设备出错:', error)
        this.$message.error('保存设备出错：' + (error.message || '浏览器网络有波动，请稍后再试'))
      }
    },
    handleDialogClosed() {
      // 重置表单
      if (this.$refs.deviceForm) {
        this.$refs.deviceForm.resetForm()
      }
      this.currentDevice = {}
    },
    getDeviceTypeLabel(deviceType) {
      const type = this.deviceTypes.find(t => t.value === deviceType)
      return type ? type.label : deviceType
    },
    getDeviceIcon(deviceType) {
      const iconMap = {
        'AIS': 'el-icon-location',
        'CAMERA': 'el-icon-video-camera',
        'RADAR': 'el-icon-view',
        'SENSOR': 'el-icon-data-analysis',
        'VHF': 'el-icon-phone-outline',
        'FLOWMETER': 'el-icon-refresh',
        'OTHER': 'el-icon-cpu'
      }
      return iconMap[deviceType] || 'el-icon-cpu'
    },
    getEnabledTooltip(enabled) {
      return enabled === 1 
        ? '当前设备已启用，点击可禁用' 
        : '当前设备已禁用，点击可启用'
    },
    // 启动设备状态轮询
    startStatusPolling() {
      // 如果已有定时器，先清除
      if (this.statusPollingTimer) {
        clearInterval(this.statusPollingTimer)
      }
      
      // 设置10秒轮询
      this.statusPollingTimer = setInterval(() => {
        this.pollDeviceStatus()
      }, 10000)
    },
    // 停止设备状态轮询
    stopStatusPolling() {
      if (this.statusPollingTimer) {
        clearInterval(this.statusPollingTimer)
        this.statusPollingTimer = null
      }
    },
    // 轮询设备状态
    async pollDeviceStatus() {
      // 只有在有桥梁ID且设备列表不为空时才轮询
      if (!this.selectedBridgeId || this.devices.length === 0) {
        return
      }
      
      try {
        const response = await deviceAPI.getDeviceStatusByBridge(this.selectedBridgeId)
        if (response.code === 200 && response.data) {
          this.updateDeviceStatus(response.data)
        }
      } catch (error) {
        // 轮询失败时不显示错误消息，避免干扰用户操作
        console.warn('设备状态轮询失败:', error)
      }
    },
    // 更新设备状态
    updateDeviceStatus(statusList) {
      if (!Array.isArray(statusList)) {
        return
      }
      
      // 创建状态映射表
      const statusMap = new Map()
      statusList.forEach(item => {
        statusMap.set(item.id, {
          status: item.status,
          enabled: item.enabled
        })
      })
      
      // 更新设备列表中的状态
      let hasChanges = false
      this.devices.forEach(device => {
        const newStatus = statusMap.get(device.id)
        if (newStatus) {
          // 检查状态是否有变化
          if (device.status !== newStatus.status || device.enabled !== newStatus.enabled) {
            device.status = newStatus.status
            device.enabled = newStatus.enabled
            hasChanges = true
          }
        }
      })
      
      // 如果有状态变化，重新计算统计数据和应用过滤器
      if (hasChanges) {
        this.calculateDeviceStats()
        this.applyFilters()
      }
    }
  }
}
</script>

<style scoped>
.device-management {
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* 防止出现不必要的滚动条 */
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

/* 确保桥梁选择器和标题垂直居中对齐 */
.align-title {
  align-items: center;
  display: flex;
}

.align-title h2,
.align-title span,
.align-title .bridge-selector {
  margin-top: auto;
  margin-bottom: auto;
}

.device-stats {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  flex: 1;
}

.stat-icon {
  font-size: 2rem;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: #f5f7fa;
}

.text-blue-500 {
  color: #409eff;
}

.text-green-500 {
  color: #67c23a;
}

.text-red-500 {
  color: #f56c6c;
}

.text-yellow-500 {
  color: #e6a23c;
}

.text-gray-500 {
  color: #909399;
}

.text-xl {
  font-size: 1.25rem;
}

.text-2xl {
  font-size: 1.5rem;
}

.font-bold {
  font-weight: bold;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mr-1 {
  margin-right: 0.25rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mr-3 {
  margin-right: 0.75rem;
}

.mr-4 {
  margin-right: 1rem;
}

.ml-4 {
  margin-left: 1rem;
}

.w-40 {
  width: 10rem;
}

.w-80 {
  width: 20rem;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}

.device-name-cell {
  display: flex;
  align-items: center;
}

.status-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.status-detail-btn {
  padding: 0;
  font-size: 12px;
}

.device-icon {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background-color: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 表格自适应剩余高度 */
.device-table {
  flex: 1;
  overflow: auto; /* 确保表格内容过多时只在表格内滚动 */
}
</style> 